/* eslint-disable max-len */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
// import { User } from '@auth0/auth0-react';
import type { BoddRegion, MerchantSummaryDto, MerchantUserContext } from '@services';
import type { RootState } from '../../../app/store';
import type { BoddNavigationItem } from '../navigation/bodd-navigation-item.model';
import {
    BoddRole,
    BoddLimitedRole,
    MerchantAdminRole,
    MerchantOwnerRole,
    QualityAssuranceRole,
    SystemOwnerRole,
} from './auth-constants';
import { Navigation } from '../navigation/navigation';

export interface PortalUser {
    userId: string | undefined,
    merchantId: number | null,
    name: string | undefined,
    email: string | undefined,
    firstName: string | undefined,
    profileImgUrl: string | undefined,
    roles: string[],
    enrolledAuthenticators?: string[],
}

const impersonatingMerchantRoles = [MerchantAdminRole];

const getSentryUser: (u: PortalUser) => {
    id: string,
    segment: string,
    username: string
} = (portalUser: PortalUser) => {
    let segment = '';
    if (portalUser.merchantId) {
        segment = `Merchant-${portalUser.merchantId}`;
    } else {
        segment = 'Bodd';
    }
    return {
        id: portalUser.userId!,
        segment,
        username: portalUser.firstName!,
    };
};

export interface AuthState {
    authenticated: boolean,
    user: PortalUser | undefined,
    loading: boolean,
    navigation: BoddNavigationItem[],
    merchantContext: MerchantUserContext | undefined,
    impersonatedMerchant: MerchantSummaryDto | null,
    switchedMerchant: MerchantSummaryDto | null,
    currentRegion: BoddRegion;

}

const initialState: AuthState = {
    authenticated: false,
    user: undefined,
    loading: false,
    navigation: Navigation.filterForRoles(null, null),
    merchantContext: undefined,
    impersonatedMerchant: null,
    switchedMerchant: null,
    currentRegion: 'AU',
};

const IMPERSONATE_KEY_NAME = 'Bodd_Merch_Imp';
const SWITCH_KEY_NAME = 'Bodd_Merch_Switch';
const REGION_KEY_NAME = 'Bodd_Region';

const storeImpersonation = (merchantId: number) => {
    sessionStorage.setItem(IMPERSONATE_KEY_NAME, String(merchantId));
};

const storeSwitch = (merchantId: number) => {
    sessionStorage.setItem(SWITCH_KEY_NAME, String(merchantId));
};

const storeRegion = (region: BoddRegion) => {
    sessionStorage.setItem(REGION_KEY_NAME, region);
};

const checkImpersonatedMerchant: (user: PortalUser, context: MerchantUserContext) => MerchantSummaryDto | null = (user: PortalUser, merchantContext: MerchantUserContext) => {
    const impersonatingId = sessionStorage.getItem(IMPERSONATE_KEY_NAME);
    if (impersonatingId && user.roles.some((r) => r === BoddRole || r === SystemOwnerRole)) {
        const id = parseInt(impersonatingId, 10);
        const merchant = merchantContext.allMerchants?.find((m) => m.id === id);
        return merchant ?? null;
    }
    return null;
};

const checkSwitchedMerchant: (user: PortalUser, context: MerchantUserContext) => MerchantSummaryDto | null = (user: PortalUser, merchantContext: MerchantUserContext) => {
    const switchedId = sessionStorage.getItem(SWITCH_KEY_NAME);
    if (switchedId && user.roles.some((r) => r === BoddRole || r === BoddLimitedRole || r === SystemOwnerRole)) {
        const id = parseInt(switchedId, 10);
        const merchant = merchantContext.allMerchants?.find((m) => m.id === id);
        return merchant ?? null;
    }
    return null;
};

const checkRegion = () => {
    const region = sessionStorage.getItem(REGION_KEY_NAME);
    if (region) {
        return region as BoddRegion;
    }
    return null;
};

const removeStoredImpersonation = () => {
    sessionStorage.removeItem(IMPERSONATE_KEY_NAME);
};

const removeStoredSwitch = () => {
    sessionStorage.removeItem(SWITCH_KEY_NAME);
};

const getConfig = (merchant: MerchantSummaryDto | undefined): { [key: string]: boolean } => ({
    qualityAssuranceHoldEnabled: !!merchant?.qualityAssuranceHoldEnabled,
    sizeMatchEnabled: !!merchant?.sizeMatchEnabled,
    cohortTrackingEnabled: !!merchant?.cohortTrackingEnabled,
});

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginCompleted: (
            state,
            action: PayloadAction<{
                user: PortalUser,
                roles: string[],
                merchantId: number | undefined,
                merchantContext: MerchantUserContext,
                enrolledAuthenticators: string[] | undefined,
            }>,
        ) => {
            state.loading = false;
            state.authenticated = true;
            const portalUser = action.payload.user;
            portalUser.roles = action.payload.roles;
            portalUser.merchantId = action.payload.merchantId ?? null;
            portalUser.enrolledAuthenticators = action.payload.enrolledAuthenticators;
            state.user = portalUser;
            state.merchantContext = action.payload.merchantContext;
            state.currentRegion = Object.keys(action.payload.merchantContext.regionMap!)[0] as BoddRegion;
            state.navigation = Navigation.filterForRoles(portalUser.roles, getConfig(action.payload.merchantContext?.userMerchant));

            const impersonatingMerchant = checkImpersonatedMerchant(portalUser, action.payload.merchantContext!);
            if (impersonatingMerchant) {
                state.impersonatedMerchant = impersonatingMerchant;
                state.currentRegion = impersonatingMerchant.region!;
                state.navigation = Navigation.filterForRoles(impersonatingMerchantRoles, getConfig(impersonatingMerchant));
            } else {
                const switchedMerchant = checkSwitchedMerchant(portalUser, action.payload.merchantContext!);
                if (switchedMerchant) {
                    state.switchedMerchant = switchedMerchant;
                    state.currentRegion = switchedMerchant.region!;
                    state.navigation = Navigation.filterForRoles(portalUser.roles, getConfig(switchedMerchant));
                } else if (portalUser.roles.some((r) => r === BoddLimitedRole)) {
                    const allMerchants = action.payload.merchantContext!.allMerchants ?? [];
                    if (allMerchants.length > 0) {
                        const lowestIdMerchant = allMerchants.reduce((min, m) => ((m.id ?? Infinity) < (min.id ?? Infinity) ? m : min));
                        state.switchedMerchant = lowestIdMerchant;
                        storeSwitch(lowestIdMerchant.id!);
                        state.currentRegion = lowestIdMerchant.region!;
                        state.navigation = Navigation.filterForRoles(portalUser.roles, getConfig(lowestIdMerchant));
                    }
                }
            }
            const region = checkRegion();
            if (region) {
                state.currentRegion = region;
            }
            Sentry.setUser(getSentryUser(portalUser));
        },
        loginStarted: (state) => {
            state.loading = true;
            state.authenticated = false;
            state.user = undefined;
            state.navigation = Navigation.filterForRoles(null, null);
            state.merchantContext = undefined;
        },
        logout: (state) => {
            Sentry.setUser(null);
            state.loading = false;
            state.authenticated = false;
            state.user = undefined;
            state.navigation = Navigation.filterForRoles(null, null);
            state.currentRegion = 'AU';
            state.merchantContext = undefined;
            state.impersonatedMerchant = null;
            state.switchedMerchant = null;
            removeStoredImpersonation();
            removeStoredSwitch();
        },
        impersonateMerchantStart: (
            state,
            action: PayloadAction<({
                merchant: MerchantSummaryDto,
            })>,
        ) => {
            state.impersonatedMerchant = action.payload.merchant;
            storeImpersonation(action.payload.merchant.id!);
            state.currentRegion = action.payload.merchant.region!;
            state.navigation = Navigation.filterForRoles(impersonatingMerchantRoles, getConfig(action.payload.merchant));
        },
        impersonateMerchantStop: (state) => {
            state.impersonatedMerchant = null;
            removeStoredImpersonation();
            state.currentRegion = Object.keys(state.merchantContext!.regionMap!)[0] as BoddRegion;
            state.navigation = Navigation.filterForRoles(state.user!.roles, getConfig(state.merchantContext?.userMerchant));
        },
        switchMerchant: (
            state,
            action: PayloadAction<({ merchant: MerchantSummaryDto })>,
        ) => {
            state.impersonatedMerchant = null;
            removeStoredImpersonation();
            state.switchedMerchant = action.payload.merchant;
            storeSwitch(action.payload.merchant.id!);
            state.currentRegion = action.payload.merchant.region!;
            state.navigation = Navigation.filterForRoles(state.user!.roles, getConfig(action.payload.merchant));
        },
        changeRegion: (state, action: PayloadAction<BoddRegion>) => {
            state.currentRegion = action.payload;
            storeRegion(action.payload);
        },
    },
});

const selectCurrentRolesInternal = (state: RootState) => (state.auth.impersonatedMerchant ? impersonatingMerchantRoles : state.auth.user?.roles ?? []);
const selectCurrentMerchantInternal = (state: RootState) => state.auth.impersonatedMerchant ?? state.auth.switchedMerchant ?? state.auth.merchantContext?.userMerchant ?? undefined;
const selectAllMerchantsInternal = (state: RootState) => state.auth.merchantContext?.allMerchants ?? [];
export const selectAllAvailableMerchants = selectAllMerchantsInternal;
const selectCurrentRegionInternal = (state: RootState) => state.auth.currentRegion;

export const selectIsAuthLoading = (state: RootState) => state.auth.loading;
export const selectIsAuthenticated = (state: RootState) => state.auth.authenticated;
export const selectAuthenticatedUser = (state: RootState) => state.auth?.user || null;
export const selectIsUserMfaSetup = (state: RootState) => {
    if ((state.auth.user?.enrolledAuthenticators?.length ?? 0) === 0) {
        return false;
    }
    if (state.auth.user!.enrolledAuthenticators!.length === 1) {
        // Users who only have EMAIL_OTP have not setup MFA
        return !state.auth.user?.enrolledAuthenticators?.includes('EMAIL_OTP');
    }
    return true;
};
export const selectHasEnrolledAuthenticators = (state: RootState) => (state.auth.user?.enrolledAuthenticators?.length ?? 0) > 0;

export const selectIsSsoUser = (state: RootState) => state.auth.user?.enrolledAuthenticators === undefined;

export const selectAllMerchants = createSelector([selectCurrentMerchantInternal, selectAllMerchantsInternal], (currentMerchant, allMerchants) => {
    if (currentMerchant) {
        return [currentMerchant];
    }
    return allMerchants;
});

export const selectMerchantNameMap = createSelector([selectAllMerchants], (merchants) => merchants.reduce((a: {
    [key: number]: string
}, c) => {
    if (typeof c?.id === 'number') {
        a[c.id] = c.name ?? '';
    }
    return a;
}, {}));

export const selectIsBoddUser = (state: RootState) => (state.auth.impersonatedMerchant ? false : state.auth.user?.roles.some((r) => r === BoddRole || r === BoddLimitedRole || r === SystemOwnerRole)) ?? false;
export const selectIsBoddLimitedUser = (state: RootState) => state.auth.user?.roles.some((r) => r === BoddLimitedRole) ?? false;
export const selectIsSwitchedMerchantActive = (state: RootState) => state.auth.switchedMerchant !== null;
export const selectCanSwitchMerchant = (state: RootState) => state.auth.user?.roles.some((r) => r === BoddRole || r === BoddLimitedRole || r === SystemOwnerRole) ?? false;
export const selectIsQaUser = (state: RootState) => state.auth.user?.roles.some((r) => r === QualityAssuranceRole) ?? false;
export const selectIsBoddUserImpersonatingMerchant = (state: RootState) => (state.auth.user?.roles.some((r) => r === BoddRole || r === SystemOwnerRole) ?? false) && state.auth.impersonatedMerchant;
export const selectNavigation = createSelector([(state: RootState) => state.auth.navigation, (state: RootState) => state.auth.currentRegion], (navigation, region) => {
    const regionPlaceholder = '%region%';
    return navigation.map((n) => ({ ...n, url: n.url.replace(regionPlaceholder, region.toLowerCase()) }));
});

// returns the merchantId - taking into account impersonation if it is active.
export const selectCurrentMerchantId = createSelector(
    [selectCurrentMerchantInternal],
    (merchantId) => merchantId?.id,
);

export const selectCurrentMerchant = createSelector([selectCurrentMerchantInternal], (merchant) => merchant);

export const selectCurrentMerchantConfigurationEnabled = (configKey?: string) => (state: RootState) => {
    if (!configKey) {
        return true;
    }
    const merchant = selectCurrentMerchantInternal(state);
    if (merchant) {
        return (merchant as any)[configKey] === true;
    }
    return true;
};

export const selectCurrentRoles = createSelector([selectCurrentRolesInternal], (roles) => roles);
export const selectCurrentRegion = createSelector([selectCurrentRegionInternal], (region) => region);
export const selectHasAdminRole = createSelector([selectCurrentRolesInternal], (roles) => roles?.some((r) => r === MerchantAdminRole || r === BoddRole || r === BoddLimitedRole || r === MerchantOwnerRole || r === SystemOwnerRole) ?? false);
export const selectHasOwnerRole = createSelector([selectCurrentRolesInternal], (roles) => roles?.some((r) => r === MerchantOwnerRole || r === SystemOwnerRole) ?? false);
export const selectHasSystemOwnerRole = createSelector([selectCurrentRolesInternal], (roles) => roles?.some((r) => r === SystemOwnerRole) ?? false);
export const selectGreatestRole = createSelector([selectCurrentRolesInternal], (roles) => {
    if (roles.length === 1) {
        return roles[0];
    }
    if (SystemOwnerRole in roles) {
        return SystemOwnerRole;
    }
    if (MerchantOwnerRole in roles) {
        return MerchantOwnerRole;
    }
    if (BoddRole in roles) {
        return BoddRole;
    }
    if (BoddLimitedRole in roles) {
        return BoddLimitedRole;
    }
    if (MerchantAdminRole in roles) {
        return MerchantAdminRole;
    }
    if (QualityAssuranceRole in roles) {
        return QualityAssuranceRole;
    }
    if (BoddRole in roles) {
        return BoddRole;
    }
    return 'None';
});

export const {
    loginStarted,
    loginCompleted,
    logout,
    impersonateMerchantStart,
    impersonateMerchantStop,
    switchMerchant,
    changeRegion,
} = authSlice.actions;

export default authSlice.reducer;
